import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchCurrentAdmin, loginAdmin, logoutAdmin } from "./authApi";
import { getAuthToken } from "./api";
import { AdminUser } from "./types";

interface AdminAuthValue {
  currentAdmin: AdminUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthValue | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const hydrate = useCallback(async () => {
    if (!getAuthToken()) {
      setCurrentAdmin(null);
      setIsLoading(false);
      return;
    }
    try {
      const admin = await fetchCurrentAdmin();
      setCurrentAdmin(admin);
    } catch {
      setCurrentAdmin(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();

    const onUnauthorized = () => setCurrentAdmin(null);
    window.addEventListener("admin:unauthorized", onUnauthorized);
    return () => window.removeEventListener("admin:unauthorized", onUnauthorized);
  }, [hydrate]);

  const login = useCallback(async (email: string, password: string) => {
    const admin = await loginAdmin(email, password);
    setCurrentAdmin(admin);
  }, []);

  const logout = useCallback(() => {
    logoutAdmin();
    setCurrentAdmin(null);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ currentAdmin, isLoading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
